import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { useTranslate } from '@growtherapy/i18n';

import speakerTestChimeUri from '../../assets/audio/speaker-test-chime.mp3?url';
import { logger } from '../../datadog/logger';
import { usePlayAudioCallback } from '../../hooks/usePlayAudioCallback';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { useNoiseCancellationCallbacks } from '../../session/controls/useNoiseCancellationCallbacks';
import { getDeviceOptionsFromDeviceList, useUpdateAvailableDevicesInterval } from '../../session/devices/useDevices';
import { useGetInputVolume } from '../../session/useGetInputVolume';
import { toast } from '../../toasts';
import {
  LocalMediaStatus,
  VideoPlatform,
  currentMicrophoneIdAtom,
  getStoredAudioVideoSettings,
  localAudioStatusAtom,
  useStoredAudioVideoSettings,
  useSwitchMicrophone,
} from '../../video-sdk';
import { useAudioOutput } from '../../video-sdk/actions/useAudioActions';
import {
  availableDevicesAtom,
  isKrispNoiseCancellationSupported as isKrispNoiseCancellationSupported_,
  permissionDeniedAtom,
} from '../../video-sdk/twilio';
import { AudioSettingsTab } from './AudioSettingsTab';
import { BlockedPermissionMessageWrapper } from './BlockedPermissionMessageWrapper';

export function AudioSettingsTabWrapper() {
  const t = useTranslate();
  useUpdateAvailableDevicesInterval();

  const devices = useAtomValue(availableDevicesAtom);
  const { microphoneIsBlocked } = useAtomValue(permissionDeniedAtom);
  const localAudioStatus = useAtomValue(localAudioStatusAtom);
  const microphoneIsLoading = !localAudioStatus || localAudioStatus === LocalMediaStatus.LOADING;
  const [isAudioOutputLoading, setAudioOutputLoading] = useState(false);

  const videoPlatformIsZoom = useVideoPlatform() === VideoPlatform.Zoom;
  const inputVolume = useGetInputVolume();
  const { audioOutputDeviceId, switchAudioOutputDevice } = useAudioOutput();
  const handleSwitchingAudioOutputDevice = useCallback(
    async (deviceId: string) => {
      setAudioOutputLoading(true);
      await switchAudioOutputDevice({
        deviceId,
      })
        .catch((error: Error) => {
          toast.error(t('telehealth-core:settings.unableToSelectSpeakerDevice'));
          logger.error('Failed to switch audio output device', { deviceId }, error);
        })
        .finally(() => {
          setAudioOutputLoading(false);
        });
    },
    [switchAudioOutputDevice, t],
  );

  const { storedAudioVideoSettings } = useStoredAudioVideoSettings();
  const selectedMicrophone = useAtomValue(currentMicrophoneIdAtom);
  const { playAudio, volume, isPlaying } = usePlayAudioCallback(speakerTestChimeUri);
  const inputOptions = getDeviceOptionsFromDeviceList(devices?.audioInputDevices);
  const outputOptions = getDeviceOptionsFromDeviceList(devices?.audioOutputDevices);
  const { enableNoiseCancellation, disableNoiseCancellation } = useNoiseCancellationCallbacks();

  const switchMicrophone = useSwitchMicrophone();

  // This debounce shouldn't be strictly necessary, but it seems like the select
  // component can call its onChange twice on Android devices which causes
  // unexpected behavior
  const setMicrophone = useDebounceCallback(
    useCallback(
      async (selectedId: string) => {
        if (!(await switchMicrophone(selectedId))) {
          toast.error(t('telehealth-core:settings.somethingWentWrongSelectingYourMicrophoneDevice'));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    100,
  );

  const toggleNoiseCancellation = useDebounceCallback(
    useCallback(
      async () => {
        const { noiseCancellationIsEnabled: noiseCancellationIsEnabled } = getStoredAudioVideoSettings();
        let result;
        if (noiseCancellationIsEnabled) {
          result = await disableNoiseCancellation();
        } else {
          result = await enableNoiseCancellation();
        }
        if (result.status === 'error') {
          toast.error(t('telehealth-core:settings.failedToToggleNoiseCancellation'));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    100,
  );

  const correspondingMicrophoneId = inputOptions.some((option) => option.value === selectedMicrophone)
    ? selectedMicrophone
    : storedAudioVideoSettings?.inputAudioDeviceId;

  return (
    <AudioSettingsTab
      blockedPermissionMessageComponent={BlockedPermissionMessageWrapper}
      inputVolume={inputVolume}
      outputVolume={volume}
      playTestAudio={() => playAudio(audioOutputDeviceId)}
      audioIsPlaying={isPlaying}
      inputOptions={inputOptions}
      outputOptions={outputOptions}
      outputIsLoading={isAudioOutputLoading}
      selectedMicrophone={correspondingMicrophoneId}
      setMicrophone={setMicrophone}
      selectedOutputDevice={audioOutputDeviceId}
      setOutputDevice={handleSwitchingAudioOutputDevice}
      microphoneIsBlocked={microphoneIsBlocked}
      microphoneIsLoading={microphoneIsLoading}
      toggleNoiseCancellation={toggleNoiseCancellation}
      noiseCancellationIsEnabled={storedAudioVideoSettings.noiseCancellationIsEnabled}
      isNoiseCancellationSupported={videoPlatformIsZoom || isKrispNoiseCancellationSupported_}
    />
  );
}
